import { Fragment, useRef, useEffect } from 'react'
import { NextPageContext } from 'next'
import { NextSeo, ArticleJsonLd } from 'next-seo'
import { useRouter } from 'next/router'
import { get, forEach } from 'lodash'
import { Box, Flex, Grid, Text, Divider } from '@chakra-ui/react'
import Prism from 'prismjs'
import Link from 'next/link'
import Image from 'next/image'
import sanitizeHtml from 'sanitize-html'

import envObj from '@/config/env'
import { Blog as BlogType } from '../types'
import { mq } from '@/lib/responsive'
import { formatDateWithTime } from '@/lib/helpers'
import { sanitizeConfig } from '../constants'
import { getBlog } from '../services'

import { LandingLayout } from '@/components/Layout/LandingLayout'
import { Colors, Container } from '@/components/Landing/shared'
import { FacebookShare } from '../components'
import { Markdown } from '../components/Blog'
import BlogItem from '@/components/Landing/Blog/BlogItem'
import { Tag } from '../components/Tag'
import withPage from '@/lib/page/withPage'
import CreatePolicyButton from '../components/CreatePolicyButton'

type BlogProps = {
  blog: BlogType
}

const Blog = (props: BlogProps) => {
  const { blog } = props

  const markdownRef = useRef<Document>()
  const router = useRouter()

  useEffect(() => {
    if (markdownRef.current) {
      forEach(markdownRef.current.querySelectorAll('pre code'), (x) => {
        x.innerHTML = Prism.highlight(
          x.innerHTML
            .replace(/&lt;/gi, '<')
            .replace(/&gt;/gi, '>')
            .replace(/&amp;/gi, '&'),
          Prism.languages.javascript,
          'javascript',
        )
      })
    }
  }, [blog.id])

  return (
    <Fragment>
      <NextSeo
        title={blog.title}
        description={blog.meta_description}
        additionalMetaTags={[
          {
            property: 'author',
            content: blog.meta_author,
          },
          {
            name: 'keywords',
            content: blog.meta_keywords,
          },
        ]}
        openGraph={{
          title: blog.meta_title,
          description: blog.meta_description,
          site_name: 'PDPAPro',
          type: 'article',
          images: [
            {
              url: blog.image_1.url,
              alt: blog.meta_title,
            },
          ],
          article: {
            publishedTime: blog.published_at,
            authors: [blog.meta_author],
            tags: (blog.meta_keywords ?? '').split(/,/i),
          },
        }}
      />
      <ArticleJsonLd
        url={`${envObj.APP_URL}${router.asPath}`}
        title={blog.meta_title}
        images={[blog.image.original]}
        datePublished={blog.published_at}
        authorName={[blog.meta_author]}
        description={blog.meta_description}
      />

      <LandingLayout isAltStyle>
        <Container
          css={{
            paddingTop: '64px',
            [mq('sm')]: { paddingTop: '108px' },
            [mq('md')]: { maxWidth: 728, paddingTop: '48px' },
          }}
        >
          <Link href="/blogs">
            <a
              css={{ display: 'block', marginBottom: '16px', fontWeight: 300 }}
            >
              Back
            </a>
          </Link>

          <h1
            css={{
              marginBottom: '20px',
              fontSize: '36px',

              [mq('sm')]: {
                fontSize: '40px',
              },
            }}
          >
            {blog.title}
          </h1>

          <Flex
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: '40px',
            }}
          >
            <time
              css={{
                color: Colors.PRIMARY_DEFAULT,
                display: 'block',
                fontWeight: 500,
                fontSize: '14px',
              }}
            >
              {formatDateWithTime(blog.published_at, 'MMMM D, YYYY')}
            </time>

            <FacebookShare />
          </Flex>

          {blog.image_1.url && (
            <Image
              title={blog.title}
              alt={blog.title}
              src={blog.image_1.url}
              objectFit="cover"
              layout="responsive"
              width={1920}
              height={1008}
            />
          )}

          <Markdown
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(blog.content, sanitizeConfig),
            }}
          />

          <FacebookShare />

          <Flex gap={2} sx={{ mt: 6, flexWrap: 'wrap' }}>
            {blog.tags.map((data, idx) => (
              <Tag key={idx} name={data.name} tagId={data.id} />
            ))}
          </Flex>

          <Flex justifyContent="center">
            <CreatePolicyButton />
          </Flex>

          <Divider mb={6} />

          <Box sx={{ paddingBottom: { base: '48px', md: '0px' } }}>
            <Text
              sx={{
                fontSize: '32px',
                color: 'primary.500',
                textAlign: { base: 'center', md: 'left' },
              }}
            >
              บทความที่เกี่ยวข้อง
            </Text>
            <Grid
              mt={6}
              gap="8px"
              gridTemplateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
            >
              {blog.recommended.map((blog) => (
                <BlogItem
                  key={blog.title}
                  image={blog.image_1.url}
                  title={blog.title}
                  date={blog.published_at}
                  link={`/blogs/${blog.slug}`}
                  tags={blog.tags}
                />
              ))}
            </Grid>
          </Box>
        </Container>
      </LandingLayout>
    </Fragment>
  )
}

Blog.getInitialProps = async (ctx: NextPageContext) => {
  const slug = encodeURI(get(ctx, 'query.slug'))

  const blog = await getBlog(slug)

  return {
    blog,
  }
}

export default withPage()(Blog)
